// IncomeTracker.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Container, Typography, Table, TableHead, TableRow,
    TableCell, TableBody, Paper, Box, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, Grid
} from '@mui/material';

const IncomeTracker = () => {
    const [selectDialogOpen, setSelectDialogOpen] = useState(false);
    const [dateTxns, setDateTxns] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
    const [editingTxn, setEditingTxn] = useState(null);
    const [deleteTxnDialog, setDeleteTxnDialog] = useState({ open: false, txn: null });
    const [deleteCompanyDialog, setDeleteCompanyDialog] = useState({ open: false, company: null });

    const [formData, setFormData] = useState({
        date: '',
        type: 'income',
        earnings: [],
        expense: { type: '', amount: '' }
    });
    const [companyForm, setCompanyForm] = useState({ name: '', type: 'sms' });
    const [withdrawDialog, setWithdrawDialog] = useState({ open: false, company: null, amount: '', date: new Date().toISOString().split('T')[0] });
    const [editWithdrawDialog, setEditWithdrawDialog] = useState({ open: false, company: null, index: null, amount: '', date: '' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, company: null, index: null });

    useEffect(() => {
        fetchTransactions();
        fetchSummary();
        fetchCompanies();
    }, []);

    const fetchTransactions = async () => {
        const res = await axios.get('http://localhost:8000/transactions');
        setTransactions(res.data);
    };

    const fetchSummary = async () => {
        const res = await axios.get('http://localhost:8000/summary/overall');
        setSummary(res.data);
    };

    const fetchCompanies = async () => {
        const res = await axios.get('http://localhost:8000/companies');
        setCompanies(res.data);
    };

    const openEditDialog = (txn) => {
        const { date, type, earnings, expenses } = txn;
        const parsedDate = new Date(date).toISOString().split('T')[0];
        setFormData({
            date: parsedDate,
            type,
            earnings: earnings || [],
            expense: {
                type: expenses?.sim ? 'sim' : 'load',
                amount: expenses?.sim || expenses?.load || ''
            }
        });
        setEditingTxn(txn);
        setTimeout(() => setOpenDialog(true), 100);
    };

    const openEditDialogSelector = (txns) => {
        setDateTxns(txns);
        setSelectDialogOpen(true);
    };

    const handleSelectEdit = (txn) => {
        setSelectDialogOpen(false);
        setTimeout(() => openEditDialog(txn), 100);
    };

    const handleSubmit = async () => {
        const data = { ...formData };
        data.date = new Date(data.date);

        if (data.type === 'expense') {
            data.expenses = {};
            if (data.expense.type === 'sim') data.expenses.sim = parseFloat(data.expense.amount);
            else if (data.expense.type === 'load') data.expenses.load = parseFloat(data.expense.amount);
            data.earnings = [];
            delete data.expense;
        } else {
            data.expenses = { sim: 0, load: 0 };
        }

        if (editingTxn) {
            await axios.put(`http://localhost:8000/transactions/${editingTxn._id}`, data);
        } else {
            await axios.post('http://localhost:8000/transactions', data);
        }

        setOpenDialog(false);
        setEditingTxn(null);
        fetchTransactions();
        fetchSummary();
    };

    const handleAddCompany = async () => {
        await axios.post('http://localhost:8000/companies', companyForm);
        setCompanyForm({ name: '', type: 'sms' });
        setOpenCompanyDialog(false);
        fetchCompanies();
    };

    const handleDeleteCompany = async (id) => {
        await axios.delete(`http://localhost:8000/companies/${id}`);
        fetchCompanies();
    };

    const handleEditWithdrawal = (companyId, index, amount, date) => {
        setEditWithdrawDialog({ open: true, company: companies.find(c => c._id === companyId), index, amount, date: date.split('T')[0] });
    };

    const handleSubmitEditWithdrawal = async () => {
        const { company, index, amount, date } = editWithdrawDialog;
        await axios.put(`http://localhost:8000/companies/${company._id}/withdraw/${index}`, {
            amount: parseFloat(amount),
            date
        });
        setEditWithdrawDialog({ ...editWithdrawDialog, open: false });
        fetchCompanies();
    };

    const handleDeleteWithdrawal = (companyId, index) => {
        setConfirmDialog({ open: true, company: companies.find(c => c._id === companyId), index });
    };

    const confirmDeleteWithdrawal = async () => {
        const { company, index } = confirmDialog;
        await axios.delete(`http://localhost:8000/companies/${company._id}/withdraw/${index}`);
        setConfirmDialog({ ...confirmDialog, open: false });
        fetchCompanies();
    };

    const handleAddWithdrawal = (company) => {
        setWithdrawDialog({ open: true, company, amount: '', date: new Date().toISOString().split('T')[0] });
    };

    const handleSubmitWithdrawal = async () => {
        const { company, amount, date } = withdrawDialog;
        if (!amount || isNaN(amount)) return alert("Invalid amount");
        await axios.post(`http://localhost:8000/companies/${company._id}/withdraw`, {
            amount: parseFloat(amount),
            date
        });
        setWithdrawDialog({ ...withdrawDialog, open: false });
        fetchCompanies();
    };

    const formatCurrency = (amount) => amount?.toLocaleString(undefined, { minimumFractionDigits: 2 });

    const groupedByDate = transactions.reduce((acc, txn) => {
        const dateKey = txn.date.split('T')[0];
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(txn);
        return acc;
    }, {});

    return (
        <Container>
            <Typography variant="h4" gutterBottom>
                Income Tracker
            </Typography>

            <Box my={2}>
                <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}>
                    Add Transaction
                </Button>
                <Button variant="outlined" sx={{ ml: 2 }} onClick={() => setOpenCompanyDialog(true)}>
                    Add Company
                </Button>
            </Box>
            <Typography variant="h6">Daily</Typography>
            <Table component={Paper}>

                <TableHead>

                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>SIM Expense (PHP)</TableCell>
                        <TableCell>LOAD Expense (PHP)</TableCell>
                        <TableCell>Total Expense (PHP)</TableCell>
                        {companies.map((c) => (
                            <TableCell key={c._id}>{c.name} ({c.type.toUpperCase()})</TableCell>
                        ))}
                        <TableCell>Gross (PHP)</TableCell>
                        <TableCell>Net Profit (PHP)</TableCell>
                        <TableCell>Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(groupedByDate).map(([date, txns]) => {
                        const earningsByCompany = {};
                        let smsTotal = 0;
                        let voipTotal = 0;
                        let sim = 0;
                        let load = 0;
                        const currencyRates = {
                            eur_to_php: summary?.sms_php / summary?.sms_total_eur || 60,
                            usd_to_php: summary?.voip_php / summary?.voip_total_usd || 56
                        };

                        companies.forEach((c) => {
                            earningsByCompany[c._id] = 0;
                        });

                        txns.forEach((txn) => {
                            txn.earnings?.forEach((e) => {
                                earningsByCompany[e.company_id] += e.amount;
                                const company = companies.find((c) => c._id === e.company_id);
                                if (company?.type === 'sms') smsTotal += e.amount * currencyRates.eur_to_php;
                                if (company?.type === 'voip') voipTotal += e.amount * currencyRates.usd_to_php;
                            });
                            sim += txn.expenses?.sim || 0;
                            load += txn.expenses?.load || 0;
                        });

                        const gross = smsTotal + voipTotal;
                        const totalExpense = sim + load;
                        const net = gross - totalExpense;

                        return (
                            <TableRow key={date}>
                                <TableCell>{date}</TableCell>
                                <TableCell>{formatCurrency(sim)}</TableCell>
                                <TableCell>{formatCurrency(load)}</TableCell>
                                <TableCell>{formatCurrency(totalExpense)}</TableCell>
                                {companies.map((c) => (
                                    <TableCell key={c._id}>{earningsByCompany[c._id] ? `${c.type === 'sms' ? 'EUR' : 'USD'} ${formatCurrency(earningsByCompany[c._id])}` : '-'}</TableCell>
                                ))}
                                <TableCell>₱{formatCurrency(gross)}</TableCell>
                                <TableCell>₱{formatCurrency(net)}</TableCell>
                                <TableCell>
                                    <Button onClick={() => openEditDialogSelector(txns)}>Edit</Button>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Dialog open={selectDialogOpen} onClose={() => setSelectDialogOpen(false)}>
                <DialogTitle>Select Transaction to Edit</DialogTitle>
                <DialogContent>
                    {dateTxns.map((txn) => {
                        const label = txn.type === 'expense'
                            ? `${txn.type.toUpperCase()} - ${txn.expenses?.sim ? 'SIM' : 'LOAD'} - ₱${txn.expenses?.sim || txn.expenses?.load}`
                            : txn.earnings.map(e => {
                                const company = companies.find(c => c._id === e.company_id);
                                return `${txn.type.toUpperCase()} - ${company?.name || 'Unknown'} - ${e.amount}`;
                            }).join(', ');
                        return (
                            <Box key={txn._id} display="flex" justifyContent="space-between" alignItems="center" my={1}>
                                <Button onClick={() => handleSelectEdit(txn)}>{label}</Button>
                                <Button
                                    color="error"
                                    onClick={() => setDeleteTxnDialog({ open: true, txn })}
                                >
                                    Delete
                                </Button>
                            </Box>

                        );
                    })}
                </DialogContent>
            </Dialog>


            <Dialog open={deleteTxnDialog.open} onClose={() => setDeleteTxnDialog({ open: false, txn: null })}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this transaction?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTxnDialog({ open: false, txn: null })}>Cancel</Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            await axios.delete(`http://localhost:8000/transactions/${deleteTxnDialog.txn._id}`);
                            setDeleteTxnDialog({ open: false, txn: null });
                            setSelectDialogOpen(false);
                            fetchTransactions();
                            fetchSummary();
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Withdraw Dialog */}
            <Dialog open={withdrawDialog.open} onClose={() => setWithdrawDialog({ ...withdrawDialog, open: false })}>
                <DialogTitle>Withdraw from {withdrawDialog.company?.name}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={withdrawDialog.amount}
                        onChange={(e) => setWithdrawDialog({ ...withdrawDialog, amount: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Date"
                        type="date"
                        value={withdrawDialog.date}
                        onChange={(e) => setWithdrawDialog({ ...withdrawDialog, date: e.target.value })}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWithdrawDialog({ ...withdrawDialog, open: false })}>Cancel</Button>
                    <Button onClick={handleSubmitWithdrawal}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Withdrawal Dialog */}
            <Dialog open={editWithdrawDialog.open} onClose={() => setEditWithdrawDialog({ ...editWithdrawDialog, open: false })}>
                <DialogTitle>Edit Withdrawal</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        value={editWithdrawDialog.amount}
                        onChange={(e) => setEditWithdrawDialog({ ...editWithdrawDialog, amount: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Date"
                        type="date"
                        value={editWithdrawDialog.date}
                        onChange={(e) => setEditWithdrawDialog({ ...editWithdrawDialog, date: e.target.value })}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditWithdrawDialog({ ...editWithdrawDialog, open: false })}>Cancel</Button>
                    <Button onClick={handleSubmitEditWithdrawal}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Delete Withdrawal Dialog */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this withdrawal?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button color="error" onClick={confirmDeleteWithdrawal}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteCompanyDialog.open} onClose={() => setDeleteCompanyDialog({ open: false, company: null })}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete the company <strong>{deleteCompanyDialog.company?.name}</strong>?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCompanyDialog({ open: false, company: null })}>Cancel</Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            await handleDeleteCompany(deleteCompanyDialog.company._id);
                            setDeleteCompanyDialog({ open: false, company: null });
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={openCompanyDialog} onClose={() => setOpenCompanyDialog(false)}>
                <DialogTitle>Add Company</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Company Name"
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                        margin="normal"
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={companyForm.type}
                            onChange={(e) => setCompanyForm({ ...companyForm, type: e.target.value })}
                        >
                            <MenuItem value="sms">SMS</MenuItem>
                            <MenuItem value="voip">VOIP</MenuItem>
                        </Select>
                    </FormControl>
                    <Box mt={2}>
                        <Typography variant="subtitle2">Existing Companies</Typography>
                        {companies.map((company) => (
                            <Box key={company._id} display="flex" justifyContent="space-between" alignItems="center" my={1}>
                                <span>{company.name} ({company.type})</span>
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteCompanyDialog({ open: true, company })}
                                >
                                    Delete
                                </Button>

                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCompanyDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddCompany}>Add</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
                <DialogTitle>{editingTxn ? 'Edit' : 'Add'} Transaction</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        >
                            <MenuItem value="income">Income</MenuItem>
                            <MenuItem value="expense">Expense</MenuItem>
                        </Select>
                    </FormControl>

                    {formData.type === 'income' && formData.earnings.map((e, idx) => {
                        const selectedCompany = companies.find(c => c._id === e.company_id);
                        const label = selectedCompany?.type === 'sms' ? 'Amount (EUR)' : 'Amount (USD)';
                        return (
                            <Grid container spacing={2} key={idx}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Company</InputLabel>
                                        <Select
                                            value={e.company_id}
                                            onChange={(event) => {
                                                const updated = [...formData.earnings];
                                                updated[idx].company_id = event.target.value;
                                                setFormData({ ...formData, earnings: updated });
                                            }}
                                        >
                                            {companies.map((c) => (
                                                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label={label}
                                        type="number"
                                        value={e.amount}
                                        onChange={(event) => {
                                            const updated = [...formData.earnings];
                                            updated[idx].amount = parseFloat(event.target.value);
                                            setFormData({ ...formData, earnings: updated });
                                        }}
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>
                        );
                    })}

                    {formData.type === 'income' && (
                        <Button onClick={() => setFormData({ ...formData, earnings: [...formData.earnings, { company_id: '', amount: '' }] })}>
                            Add Company Earning
                        </Button>
                    )}

                    {formData.type === 'expense' && (
                        <>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Expense Type</InputLabel>
                                <Select
                                    value={formData.expense.type}
                                    onChange={e => setFormData({ ...formData, expense: { ...formData.expense, type: e.target.value } })}
                                >
                                    <MenuItem value="sim">SIM</MenuItem>
                                    <MenuItem value="load">LOAD</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Amount (PHP)"
                                type="number"
                                value={formData.expense.amount}
                                onChange={e => setFormData({ ...formData, expense: { ...formData.expense, amount: e.target.value } })}
                                margin="normal"
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSubmit}>Save</Button>
                </DialogActions>
            </Dialog>
            <Box my={4}>
                <Typography variant="h6">Company Balances</Typography>
                <Table component={Paper} sx={{ mt: 2 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Company</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Earned</TableCell>
                            <TableCell>Withdrawn</TableCell>
                            <TableCell>Balance</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {companies.map((company) => {
                            const earnings = transactions.flatMap(t => t.earnings || [])
                                .filter(e => e.company_id === company._id)
                                .reduce((sum, e) => sum + e.amount, 0);
                            const withdrawn = (company.withdrawals || []).reduce((sum, w) => sum + w.amount, 0);
                            const balance = earnings - withdrawn;

                            return (
                                <TableRow key={company._id}>
                                    <TableCell>{company.name}</TableCell>
                                    <TableCell>{company.type.toUpperCase()}</TableCell>
                                    <TableCell>{company.type === 'sms' ? 'EUR' : 'USD'} {formatCurrency(earnings)}</TableCell>
                                    <TableCell>{company.type === 'sms' ? 'EUR' : 'USD'} {formatCurrency(withdrawn)}</TableCell>
                                    <TableCell>{company.type === 'sms' ? 'EUR' : 'USD'} {formatCurrency(balance)}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => handleAddWithdrawal(company)}
                                        >
                                            Withdraw
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                <Box mt={4}>
                    <Typography variant="h6">Overall Summary</Typography>
                    <Table component={Paper} sx={{ mt: 1 }}>
                        <TableBody>
                            <TableRow>
                                <TableCell>Current Balance</TableCell>
                                <TableCell>
                                    ₱{formatCurrency((companies || []).reduce((sum, c) => {
                                        const earnings = transactions.flatMap(t => t.earnings || [])
                                            .filter(e => e.company_id === c._id)
                                            .reduce((s, e) => s + e.amount, 0);
                                        const withdrawn = c.withdrawn || 0;
                                        const rate = c.type === 'sms'
                                            ? (summary?.sms_php / summary?.sms_total_eur || 60)
                                            : (summary?.voip_php / summary?.voip_total_usd || 56);
                                        return sum + (earnings - withdrawn) * rate;
                                    }, 0))}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Total Gross Earnings</TableCell>
                                <TableCell>₱{formatCurrency(summary?.sms_php + summary?.voip_php)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Total Expenses</TableCell>
                                <TableCell>₱{formatCurrency(summary?.total_expense)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Total Withdrawn</TableCell>
                                <TableCell>
                                    ₱{formatCurrency((companies || []).reduce((sum, c) => {
                                        const withdrawn = c.withdrawn || 0;
                                        const rate = c.type === 'sms' ? (summary?.sms_php / summary?.sms_total_eur || 60) : (summary?.voip_php / summary?.voip_total_usd || 56);
                                        return sum + withdrawn * rate;
                                    }, 0))}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Net Profit</TableCell>
                                <TableCell>
                                    ₱{formatCurrency((summary?.sms_php + summary?.voip_php) - summary?.total_expense)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            </Box>

            <Box mt={4}>
                <Typography variant="h6">Withdrawals</Typography>
                <Table component={Paper} sx={{ mt: 2 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {companies.map((company) => (
                            (company.withdrawals || []).map((w, index) => (
                                <TableRow key={`${company._id}-${index}`}>
                                    <TableCell>{new Date(w.date).toLocaleDateString()}</TableCell>
                                    <TableCell>{company.name}</TableCell>
                                    <TableCell>{company.type.toUpperCase()}</TableCell>
                                    <TableCell>{company.type === 'sms' ? 'EUR' : 'USD'} {formatCurrency(w.amount)}</TableCell>
                                    <TableCell>
                                        <Button size="small" onClick={() => handleEditWithdrawal(company._id, index, w.amount, w.date)}>Edit</Button>
                                        <Button size="small" color="error" onClick={() => handleDeleteWithdrawal(company._id, index)}>Delete</Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Container>
    );
};

export default IncomeTracker;
